import type { TEmployeeAccess } from './sellerEmployees'

export interface ISellersQueryRequest
  extends Components.Schemas.IAdminSellersQueryRequest {
  // Через запятую: ACTIVE, DISABLED и причины отключения
  disable_state?: string
  // ACTIVE, INACTIVE или DISABLED — эти продавцы первыми
  status_first?: string
}
export interface ISellersQueryResponse
  extends Omit<Components.Schemas.IAdminSellersQueryResponse, 'sellers'> {
  sellers: ISeller[]
}
export interface ISellersQueryFilters
  extends Omit<ISellersQueryRequest, 'skip' | 'limit'> { }
export interface IActionsSellersRequest
  extends Components.Schemas.IActionsSellersRequest { }
export interface IAdminCreateSellerRequest
  extends Components.Schemas.IAdminCreateSellerRequest {
  default_tax?: number
}
export interface IAdminCreatePhysicalSellerRequest
  extends Components.Schemas.IAdminCreatePhysicalSellerRequest { }
export interface IAdminUpdateSellerRequest extends Components.Schemas.IUpdateSellerRequest {
  default_tax?: number
}
// Переключатель «Скрыть товары» сохраняется сразу, отдельно от профиля
export interface IUpdateSellerHiddenRequest {
  is_hidden: boolean
  // Причина скрытия: бэкенд требует её, когда переключатель включают
  hidden_comment?: string
}
export type TSellerDisableInfo = Pick<
  ISeller,
  'is_disabled' | 'disabled_reasons' | 'disabled_for_cancels_until' | 'manual_disable'
>
// is_disabled — есть хоть одна причина. MANUAL, CANCELS и PROFILE_INCOMPLETE
// скрывают магазин, STOCK_OUTDATED только снимает товары с продажи.
// PROFILE_INCOMPLETE — срок за отмены истёк, а рабочего способа доставки нет
export enum ESellerDisableReason {
  MANUAL = 'MANUAL',
  CANCELS = 'CANCELS',
  STOCK_OUTDATED = 'STOCK_OUTDATED',
  PROFILE_INCOMPLETE = 'PROFILE_INCOMPLETE'
}
// Чего не хватает до активного неотключённому магазину
export enum ESellerInactiveReason {
  NO_PRODUCTS = 'NO_PRODUCTS',
  NO_VISITS = 'NO_VISITS'
}
// Кто включил «Скрыть товары продавца»: владелец и сотрудник — продавец
export enum ESellerManualDisableBy {
  ADMIN = 'ADMIN',
  SELLER = 'SELLER'
}
// Есть, пока стоит причина MANUAL. У скрытых до 15.10.2026 не записано
export interface ISellerManualDisable {
  by: ESellerManualDisableBy
  comment: string
}
// Акция «сниженная комиссия»: rate действует до end_date
export interface ICommissionDiscount {
  is_active: boolean
  rate: number | null
  end_date: string | null
}
export interface ISeller extends Components.Schemas.ISeller {
  holidaysIsActive?: boolean
  yandex_dropoff_station_id?: string | null
  ozon_dropoff_point_id?: number | null
  ozon_shipment_method_id?: number | null
  logistics?: {
    yandex_dropoff_station_id?: string | null
    ozon_dropoff_point_id?: number | null
    ozon_shipment_method_id?: number | null
  }
  // приходит только при запросе списка с with_products_flag
  has_published_products?: boolean
  commission_discount?: ICommissionDiscount
  // отмены продавца за 30 дней и надбавка за них к комиссии, п.п.
  cancel_count?: number
  commission_penalty?: number
  disabled_reasons?: ESellerDisableReason[]
  // срок отключения за отмены: до него включает только админ площадки, потом бэкенд сам
  disabled_for_cancels_until?: string | null
  manual_disable?: ISellerManualDisable | null
  // приходят только в списке продавцов админки
  stock_state?: ISellerStockState | null
  inactive_reasons?: ESellerInactiveReason[]
  completed_orders_count?: number
  // ставка НДС для новых товаров продавца; null — ещё не выбрана
  default_tax?: number | null
  // заявки на вывод подаёт бэкенд, как только продажа становится доступна к выплате
  auto_withdrawal?: boolean
  bot_api_token?: string | null
  max_chatId?: number | null
  max_bot_link?: string | null
  balance_info: Components.Schemas.ISeller['balance_info'] & {
    retained_balance?: number
    withdrawal_requested?: number
    pending_withdrawal?: number
  }
  user_id: Components.Schemas.ISeller['user_id'] & {
    // флагов пока нет в типах пакета, но бэкенд отдаёт их вместе с продавцом.
    // is_activated здесь и есть «почта подтверждена» (.AUTH_RULES.md §1)
    is_phone_verified?: boolean
    is_activated?: boolean
  }
  moy_sklad?: Components.Schemas.ISeller['moy_sklad'] & {
    sale_fields?: string[]
    select_sale_field?: string | null
  }
  one_c?: IOneCSettingsResponse | null
  sms_notifications?: ISellerSmsNotifications
  telegram_notifications?: ISellerBotNotifications
  max_notifications?: ISellerBotNotifications
  // приходит только сотруднику магазина: до какого времени открыты разделы профиля
  employee_access?: TEmployeeAccess
}

// у продавцов, заведённых до появления настройки, поля нет — берём значение по умолчанию
export interface ISellerNotificationTopics {
  new_order: boolean
  stock_reminder: boolean
  products_disabled: boolean
  shipping_overdue: boolean
}

export type ISellerSmsNotifications = ISellerNotificationTopics

// у каждого мессенджера свой набор: продавец может подключить и Telegram, и MAX
export type ISellerBotNotifications = ISellerNotificationTopics
export interface IShop extends Components.Schemas.IShop { }
export interface IShopDetails extends Components.Schemas.IShopDetails { }
export interface ICreateSellerShopRequest
  extends Components.Schemas.ICreateSellerShopRequest { }
// own_delivery ещё нет в сгенерированных типах @mivis/petmart-api
export interface IDeliveryWays extends Components.Schemas.IDeliveryWays {
  own_delivery?: boolean
}
export interface IAdminUpdateDeliveryWaysRequest
  extends Components.Schemas.IAdminUpdateDeliveryWaysRequest { }
export interface IOwnDeliveryZone {
  latitude: number
  longitude: number
  address: string
  radius_km: number
}
// Габариты — одной упакованной единицы товара, вес — всего заказа
export interface IOwnDeliverySettings {
  max_length_cm: number
  max_width_cm: number
  max_height_cm: number
  max_weight_kg: number
  zone: IOwnDeliveryZone
}
export interface IOwnDelivery {
  enabled: boolean
  settings: IOwnDeliverySettings | null
}
export interface IAdminUpdateOwnDeliveryRequest extends IOwnDelivery {
  seller_id: string
}
export interface INearestPointOfCdek
  extends Components.Schemas.INearestPointOfCdek { }
export interface IUpdateNearestPointOfCdekRequest
  extends Components.Schemas.IAdminUpdateNearestPointOfCdekRequest { }
export interface IAdminGetChartDataRequest
  extends Components.Schemas.IAdminGetChartDataQueryRequest { }
export interface IAdminGetChartDataResponse
  extends Components.Schemas.IAdminGetChartDataResponse { }
export interface IAdminChangeCommissionRequest
  extends Components.Schemas.IAdminChangeCommissionRequest { }
export interface IAdminChangeCommissionResponse
  extends Components.Schemas.IAdminChangeCommissionResponse { }
export interface IAdminChangeCommissionDiscountRequest {
  is_active: boolean
  rate?: number | null
  end_date?: string | null
}
export interface IAdminChangeCommissionDiscountResponse {
  commission_discount: ICommissionDiscount
}
// Настройки продавца, доступные только админу площадки.
// null в сроках — общий срок площадки
export interface IAdminSellerSettings {
  isFreeDeliveryToPvz: boolean
  skip_order_deadlines: boolean
  order_confirmation_deadline_hours: number | null
  order_shipping_deadline_hours: number | null
  skip_cancel_penalty?: boolean
  exclude_from_analytics?: boolean
}
export interface IAdminSellerSettingsRequest
  extends Partial<IAdminSellerSettings> { }
// average_hours — null, когда у продавца нет заказов с отметками приёма и отправки
export interface IAdminSellerShippingTime {
  average_hours: number | null
  orders_count: number
}
export interface IAdminChangeSellerStatusRequest
  extends Components.Schemas.IAdminChangeSellerStatusRequest { }
export interface IGetInfoOfLegalRequest
  extends Components.Schemas.IGetInfoOfLegalRequest { }
export interface IGetInfoOfLegalResponse
  extends Components.Schemas.ISuggestion { }
export interface IAdminCreateWithdrawalRequest
  extends Components.Schemas.IAdminCreateWithdrawalRequest { }
export interface IIntegrationStatusRequest
  extends Paths.RequestsControllerFindOne.QueryParameters { }
export interface IIntegrationStatusResponse
  extends Components.Schemas.RequestDto { }
export interface IImportMoySkladProductsRequest
  extends Components.Schemas.MoySkladImportDto { }
export interface MoySkladLinkerRequest
  extends Paths.MoySkladLinkerControllerFindAll.QueryParameters { }
export interface MoySkladLinkerResponse
  extends Components.Schemas.MoySkladLinkerResponseDto { }
export interface MoySkladLinker extends Components.Schemas.MoySkladLinkerDto { }
export interface MoySkladLinksRequest
  extends Components.Schemas.MoySkladLinksDto { }
export interface MoySkladLink extends Components.Schemas.MoySkladLinkDto { }
export interface IMoySkladGetLinkersRequest {
  seller_id: string
  skip: string
  limit: string
}
export interface IMoySkladLinkerItem {
  _id: string
  createdAt: string
  updatedAt: string
  pathName: string
  seller_id: string
  category_id: string | null
  subCategory_id: string | null
  doNotDisplay: boolean
  externalId: string
  parentExternalId: string | null
  product_ids: string[]
}
export interface IMoySkladGetLinkersResponse {
  data: IMoySkladLinkerItem[]
  total: number
}
export interface IMoySkladSaveTokenRequest {
  token: string
  seller_id: string
}
export interface IMoySkladRefreshGroupsRequest {
  seller_id: string
}
export interface IMoySkladSaveLinkerItem {
  linker_id: string
  category_id: string
  subCategory_id: string
  doNotDisplay: boolean
}
export interface IMoySkladSaveLinkersRequest {
  updateData: IMoySkladSaveLinkerItem[]
  seller_id: string
}
export interface IMoySkladSaveAndSyncRequest {
  seller_id: string
}

export interface IMoySkladSelectSaleFieldRequest {
  select_sale_field: string
}

export interface IMoySkladSyncStatusRequest {
  seller_id: string
}

export interface IMoySkladSyncStatusResponse {
  status: string | null
  processed?: number
  totalCount?: number
  draftCount?: number
  notPublishedCount?: number
  disabledCount?: number
  reportUrl?: string | null
  runId?: string | null
  completedAt?: string | null
}

export interface IUpdateOneCCreateNewProductsRequest {
  create_new_products: boolean
  fill_description_on_create?: boolean
  seller_id?: string
}

export interface IOneCSettingsResponse {
  last_sync_at: string | null
  create_new_products: boolean
  fill_description_on_create: boolean
  token: string | null
}

export type TInsalesConnectionType = 'SHOWCASE' | 'API'

export interface IInsalesSettingsResponse {
  connection_type: TInsalesConnectionType
  shop_url: string | null
  api_key: string | null
  has_api_password: boolean
  price_field: string | null
  stock_field: string | null
  price_fields: string[]
  stock_fields: string[]
  create_new_products: boolean
  fill_description_on_create: boolean
  last_sync_at: string | null
  last_sync_error_at: string | null
  last_sync_error: string | null
}

// InSales отдаёт только ключи полей, без названий складов и типов цен,
// поэтому продавец выбирает нужное поле по статистике его значений
export interface IInsalesFieldStat {
  key: string
  filled: number
  sum: number | null
  min: number | null
  max: number | null
  sample: string | null
}

export interface IInsalesProbeResponse {
  shop_url: string
  connection_type: TInsalesConnectionType
  products_total: number
  variants_total: number
  price_fields: IInsalesFieldStat[]
  stock_fields: IInsalesFieldStat[]
  matched: number
  only_in_shop: number
  only_on_platform: number
}

export interface IInsalesSyncResponse {
  shop_url: string
  products_total: number
  matched: number
  price_updated: number
  stock_updated: number
  unchanged: number
  created: number
  created_draft: number
  only_in_shop: number
  only_on_platform: number
  synced_at: string
}

export interface IUpdateInsalesSettingsRequest {
  connection_type?: TInsalesConnectionType
  shop_url?: string
  api_key?: string | null
  api_password?: string | null
  price_field?: string | null
  stock_field?: string | null
  create_new_products?: boolean
  fill_description_on_create?: boolean
}

export interface IProbeInsalesRequest {
  shop_url: string
  connection_type?: TInsalesConnectionType
  api_key?: string | null
  api_password?: string | null
}

export interface IImportExcelHistoryRequest {
  skip: string
  limit: string
  seller_id?: string
}

export interface ILastExcelStockUpdateResponse {
  lastUpdatedAt: string | null
}

export type TStockConfirmationSource =
  | 'MANUAL'
  | 'EXCEL'
  | 'MOYSKLAD'
  | 'ONE_C'
  | 'API'
  | 'INSALES'

export interface IStockConfirmationStatus {
  sellerId: string
  lastConfirmedAt: string | null
  source: TStockConfirmationSource | null
  lastConfirmedSource: TStockConfirmationSource | null
  daysWithoutConfirmation: number | null
  disableAfterDays: number
  possibleDisableOnDay: number
  isOutdated: boolean
  isMoySkladConnected: boolean
  isMoySkladSyncEnabled: boolean
  // Товары магазина сняты с витрины: магазин отключён или сработала проверка остатков
  isStoreHidden?: boolean
  // Единственная причина — продавец скрыл товары сам
  isHiddenBySeller?: boolean
}

// days — null, когда отсчитывать не от чего: остатки не обновлялись и товаров нет
export interface ISellerStockState {
  lastConfirmedAt: string | null
  source: TStockConfirmationSource | null
  days: number | null
  disableAfterDays: number
  isOutdated: boolean
}

// sellers_count — магазины с такими заказами, orders_count — заказы в расчёте
export interface IAdminSellersOrderTimeSummary {
  average_minutes: number | null
  sellers_count: number
  orders_count: number
}

// Счётчики — по подтверждённым продавцам
export interface IAdminSellersSummaryApiSeller {
  number: number | null
  shop_name: string | null
  confirmed_at: string | null
}

export interface IAdminSellersSummary {
  approved_count: number
  // не отключены, есть опубликованный товар и заходили в кабинет за 30 дней;
  // остальные неотключённые — без опубликованных товаров или без входа за 30 дней
  active_count: number
  enabled_no_products_count: number
  enabled_no_visits_count: number
  // скрытые магазины; disabled_for_cancels_count — те из них, у кого среди
  // причин есть отмены, disabled_profile_incomplete_count — остальные
  // с незаполненным профилем
  disabled_count: number
  disabled_for_cancels_count: number
  disabled_profile_incomplete_count: number
  // отключены только за просроченные остатки
  stock_outdated_count: number
  visited_today_count: number
  visited_30_days_count: number
  confirmation_time: IAdminSellersOrderTimeSummary
  shipping_time: IAdminSellersOrderTimeSummary
  // как в шкале активности: сначала отключённые (stock_disabled_count), затем
  // неотключённые без опубликованных товаров (no_products_count), остальные —
  // по последнему способу обновления остатков или в stock_never_updated_count
  stock_disabled_count: number
  stock_sources: Record<TStockConfirmationSource, number>
  // кто входит в stock_sources.API — свежие сверху
  api_sellers: IAdminSellersSummaryApiSeller[]
  stock_never_updated_count: number
  no_products_count: number
}

// average_minutes — null, когда ни на одно обращение в окне не ответили;
// buyers_count — покупатели с обращениями в окне, answered_buyers_count — те
// из них, кому ответили хотя бы на одно
export interface IAdminSellerResponseTime {
  average_minutes: number | null
  answered_buyers_count: number
  buyers_count: number
}

// По текущему статусу заказа, за всё время. returned — отказы и возвраты
export interface IAdminSellerActivityOrders {
  completed: number
  canceled_by_seller: number
  returned: number
}

// Заходы в кабинет за последний месяц; per_day = count / days, до десятых
export interface IAdminSellerCabinetVisits {
  count: number
  days: number
  per_day: number
}

export interface IAdminSellerActivity {
  stock: IStockConfirmationStatus
  is_disabled: boolean
  disabled_reasons: ESellerDisableReason[]
  // Начало текущего отключения. null у отключённых до 28.06.2026 — раньше
  // логов нет; бэкенд без этого поля его не присылает
  disabled_at?: string | null
  manual_disable: ISellerManualDisable | null
  inactive_reasons: ESellerInactiveReason[]
  // Действия в кабинете копятся с появления поля: null — с тех пор не было
  last_activity_at: string | null
  // Бэкенд без подсчёта заходов поле не присылает
  cabinet_visits?: IAdminSellerCabinetVisits
  last_shipment: {
    // is_exact: false — время отправки не записано, в at дата самого заказа
    at: string | null
    is_exact: boolean
    order_number: number | null
  }
  shipping_time: IAdminSellerShippingTime
  // Бэкенд без расчёта времени ответа поле не присылает
  response_time?: IAdminSellerResponseTime
  published_products: number
  last_product_created_at: string | null
  // Бэкенд без подсчёта заказов поле не присылает
  orders?: IAdminSellerActivityOrders
}

export interface IImportExcelHistoryItem {
  _id?: string
  id?: string
  createdAt?: string
  created_at?: string
  fileName?: string
  file_name?: string
  originalName?: string
  name?: string
  status?: string
  state?: string
  updatedCount?: number
  updated_count?: number
  importedCount?: number
  imported_count?: number
  errorCount?: number
  errors_count?: number
  errorRows?: number
  updateRows?: number
  total?: number
  result?: string
  reportUrl?: string
  report_url?: string
  reportFileId?: string
  report_file_id?: string
  excelFile?: string
}

export interface IImportExcelHistoryResponse {
  data?: IImportExcelHistoryItem[] | IImportExcelHistoryItem
  items?: IImportExcelHistoryItem[]
  total?: number
  count?: number
}

export interface IHiddenCategoryReference {
  _id: string
  name: string
}

export interface ISellerHiddenCategoryRecord {
  _id: string
  seller_id: string
  category_id: IHiddenCategoryReference | string | null
  subCategory_id: IHiddenCategoryReference | string | null
  hidden_by: 'ADMIN' | 'SELLER'
  createdAt: string
  updatedAt: string
}

export interface ISaveSellerHiddenCategoriesRequest {
  hidden_categories: string[]
  hidden_subcategories: string[]
}

// Сколько опубликованных товаров и вариаций снимет с витрины сохранение
export interface IHiddenCategoriesPreviewResponse {
  disabled_count: number
}

export interface ISendNotifications
  extends Components.Schemas.ISendNotifications { }
export interface IShopWorkTime extends Components.Schemas.IShopWorkTime { }

export enum ENotificationRecipients {
  ALL = 'ALL',
  SELLERS = 'SELLERS',
  USERS = 'USERS'
}

export enum ESellerStatus {
  WAITING_APPROVE = 'WAITING_APPROVE',
  APPROVED = 'APPROVED',
  DECLINED = 'DECLINED',
  ARCHIVED = 'ARCHIVED'
}

// Данные о магазине, которые бэкенд отдаёт без проверки статуса: на них живёт
// экран заявки, когда весь остальной кабинет закрыт
export interface ISellerStatusResponse {
  status: ESellerStatus | 'NOT_CREATED'
  shop_name: string | null
  reason_of_cancel: string | null
}

export enum ESellerTypes {
  PHYSICAL = 'PHYSICAL',
  LEGAL = 'LEGAL'
}

export enum ESellerLegalTypes {
  LEGAL = 'LEGAL',
  INDIVIDUAL = 'INDIVIDUAL'
}

export interface ISellerCreateAuthForm {
  legalType: ESellerLegalTypes
  firstName: string
  lastName: string
  middleName?: string
  noMiddleName: boolean
  legalINN: string
  phone: string
  edoOperator?: string
  edoId?: string
  legalAddress?: string
  actualAddress?: string
  shopName: string
}

export interface ISellerFormData {
  type: ESellerTypes
  // ИП или юрлицо: на бэкенд не уходит, нужен только для выбора в форме создания
  legalType?: ESellerLegalTypes
  firstName: string
  lastName: string
  middleName?: string
  noMiddleName: boolean
  email: string
  password: string
  phone: string
  passportData?: string
  birthDate?: any
  passportPhoto?: any
  physicalINN?: string
  legalINN?: string
  companyName?: string
  OKPO?: string
  legalKPP?: string
  OGRN?: string
  organizationType?: ESellerLegalTypes
  legalAddress?: string
  registrationAddress?: string
  actualAddress?: string
  bankName?: string
  bankBIK?: string
  physicalKPP?: string
  bankPC?: string
  bankKC?: string
  cardNumber?: string
  edoOperator?: string
  edoId?: string
  shopName: string
  defaultTax?: number
  desc: string
  logo: any
  banner: any
  holidaysIsActive?: boolean
}

export interface IUpdateSellerHolidaysRequest {
  holidaysIsActive: boolean
  seller_id?: string
}

export interface IUpdateSellerHolidaysResponse {
  holidaysIsActive: boolean
}

export type TVariantSeller = 'seller' | 'profile'

export enum ECheckSellerDataTypes {
  SUCCESS = 'success',
  ERROR_LEGAL_INN = 'errorLegalINN',
  INN_NOT_FOUND = 'errorPhysicalINNNotFound',
  INVALID_PASSPORT = 'errorPhysicalPassport',
  INCORRECT_INN = 'errorPhysicalIncorrectINN'
}

export enum ECheckForFillingSellerDataError {
  SHOP_IS_NOT_EXIST = 'SHOP_IS_NOT_EXIST',
  CDEK_POINT_IS_NOT_EXIST = 'CDEK_POINT_IS_NOT_EXIST',
  DELIVERY_WAYS_IS_NOT_EXIST = 'DELIVERY_WAYS_IS_NOT_EXIST',
  SELLER_INFO_IS_NOT_FILLED_OUT = 'SELLER_INFO_IS_NOT_FILLED_OUT',
  BANKING_DETAILS_IS_NOT_FILLED_OUT = 'BANKING_DETAILS_IS_NOT_FILLED_OUT'
}
